/**
 * twoPointGesture – scale and turn a model with two points: two hands, two
 * controllers or two fingers.
 *
 * Every input the student has reduces to the same two numbers per frame: how
 * far apart the two points are, and which way the line between them points.
 * Spreading them scales the model by the same ratio; twisting them turns it by
 * the same angle. Hands and controllers measure in metres on the floor plane,
 * touch measures in pixels on the screen, and none of that matters here — only
 * ratios and angle CHANGES are used, so the units cancel out.
 *
 * Three things make the naive version feel broken, and are why this is a module
 * with tests rather than four lines inline in the player:
 *
 *   - Angles from atan2 jump from +π to -π when the line between the points
 *     swings through "straight behind". Subtracting the start angle from the
 *     current one then reads a small twist as a whole turn, and the model spins
 *     round in a single frame. Twist is therefore accumulated from per-frame
 *     changes, each wrapped into (-π, π], so it is continuous however far the
 *     student turns.
 *   - Once the scale reaches a limit, carrying on spreading does nothing — but
 *     measured from the original span, the student then has to bring their
 *     hands all the way back past the point where the limit was hit before the
 *     model responds again, which reads as a dead control. Hitting a limit
 *     re-anchors the gesture there, so reversing responds at once.
 *   - Hand tracking drops out: a joint goes missing, or two hands held one
 *     above the other have no meaningful floor-plane angle. A bad sample holds
 *     the current value, and the next good one re-anchors instead of jumping by
 *     whatever changed while tracking was lost.
 *
 * Pure and dependency-free (plain {x, y, z} in, plain numbers out; a
 * THREE.Vector3 satisfies the same shape), so it runs under node:test without a
 * renderer. See tests/twoPointGesture.test.mts.
 */

/**
 * How far a student may shrink or grow a model, as a multiple of the size the
 * dock gave it. Relative to the dock rather than absolute because assets arrive
 * at wildly different authored sizes; the dock has already made each one
 * comfortable to look at, so "0.4x to 3x of comfortable" means the same thing
 * for a heart and for a volcano. Below 0.4x parts become too small to point at;
 * above 3x the model swallows the student's view and the lesson panel.
 */
export const STUDENT_SCALE_LIMITS: ScaleLimits = { min: 0.4, max: 3.0 };

/**
 * Below this floor-plane separation the angle between two points is noise:
 * hands held one above the other have no meaningful heading, and atan2 of two
 * near-zero numbers swings wildly as they tremble.
 */
export const MIN_TWIST_SPAN_M = 0.05;

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

export interface ScaleLimits {
  min: number;
  max: number;
}

/** One frame's reading of the two points. */
export interface TwoPointSample {
  /** Separation, in any unit, as long as it is the same unit for the whole gesture. */
  distance: number;
  /**
   * Heading of the line between the points, in radians. Increasing angle turns
   * the model the way increasing `rotation.y` does in three.js (counter-
   * clockwise seen from above). Non-finite means "no usable angle this frame".
   */
  angle: number;
}

export interface ScaleYaw {
  scale: number;
  yaw: number;
}

/**
 * Mutable per-gesture state. Create it with `beginTwoPoint` when the second
 * point arrives and drop it when either point leaves; `updateTwoPoint` edits it
 * in place and returns it (its `scale` and `yaw` are the output), so a gesture
 * allocates nothing per frame.
 */
export interface TwoPointState {
  /** Span and scale the current ratio is measured from; moves when a limit is hit. */
  anchorDistance: number;
  anchorScale: number;
  /** Yaw when the gesture began; `twist` is added to it. */
  startYaw: number;
  /** Last usable angle, for per-frame unwrapping. NaN until one arrives. */
  lastAngle: number;
  /** Total twist since the gesture began, unwrapped — may exceed ±π. */
  twist: number;
  /** Latest output, held through bad samples. */
  scale: number;
  yaw: number;
}

const TWO_PI = Math.PI * 2;

/** Wrap an angle difference into (-π, π], the shortest way round. */
function wrapAngle(delta: number): number {
  const wrapped = delta - TWO_PI * Math.floor((delta + Math.PI) / TWO_PI);
  // floor() lands exactly-π on -π; either is a half turn, but keep the
  // documented (-π, π] so the sign of a half turn is stable.
  return wrapped === -Math.PI ? Math.PI : wrapped;
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * Start a two-point gesture from the first sample and the model's current
 * scale and yaw. `current.scale` is in whatever unit the caller will clamp in —
 * the student path passes the multiplier on the dock scale, with
 * STUDENT_SCALE_LIMITS.
 *
 * An unusable starting scale falls back to 1 (the dock fit) and an unusable
 * yaw to 0. The student path computes its multiplier as `scale.x /
 * userData.dockScale`, and dockScale only exists on assets the dock has
 * placed, so a gesture on any other asset starts from NaN. Bad samples are
 * held further down, but a bad START would never recover: NaN times any span
 * ratio is NaN, clamp() lets NaN through, and a NaN scale makes three.js draw
 * nothing, so the model would vanish for the rest of the gesture. A zero,
 * negative or infinite scale is no better a base for a ratio, so it falls back
 * too.
 */
export function beginTwoPoint(sample: TwoPointSample, current: ScaleYaw): TwoPointState {
  const usableDistance = Number.isFinite(sample.distance) && sample.distance > 0;
  const scale = Number.isFinite(current.scale) && current.scale > 0 ? current.scale : 1;
  const yaw = Number.isFinite(current.yaw) ? current.yaw : 0;
  return {
    anchorDistance: usableDistance ? sample.distance : NaN,
    anchorScale: scale,
    startYaw: yaw,
    lastAngle: Number.isFinite(sample.angle) ? sample.angle : NaN,
    twist: 0,
    scale,
    yaw,
  };
}

/**
 * Advance the gesture by one sample and return the scale and yaw to apply.
 *
 * Scale is the anchor scale times the span ratio, clamped to `limits`; yaw is
 * the start yaw plus the accumulated twist, unclamped (a model may turn round
 * as many times as the student likes). A scale that started outside `limits`
 * snaps inside on the first usable sample.
 *
 * The result is `state` itself, not a copy, so the per-frame path allocates
 * nothing. Read its numbers straight away rather than keeping the object: the
 * next update overwrites them.
 */
export function updateTwoPoint(
  state: TwoPointState,
  sample: TwoPointSample,
  limits: ScaleLimits
): ScaleYaw {
  const distance = sample.distance;
  if (Number.isFinite(distance) && distance > 0) {
    if (!(state.anchorDistance > 0)) {
      // First usable span, or the first after a dropout: measure from here.
      state.anchorDistance = distance;
      state.anchorScale = state.scale;
    }
    const raw = state.anchorScale * (distance / state.anchorDistance);
    const scale = clamp(raw, limits.min, limits.max);
    if (scale !== raw) {
      state.anchorDistance = distance;
      state.anchorScale = scale;
    }
    state.scale = scale;
  } else {
    state.anchorDistance = NaN;
  }

  if (Number.isFinite(sample.angle)) {
    if (Number.isFinite(state.lastAngle)) {
      state.twist += wrapAngle(sample.angle - state.lastAngle);
    }
    state.lastAngle = sample.angle;
    state.yaw = state.startYaw + state.twist;
  } else {
    state.lastAngle = NaN;
  }

  return state;
}

/**
 * The sample for two points in the room (hands or controllers).
 *
 * Distance is the full 3D separation, so spreading the hands vertically scales
 * as well as spreading them sideways. The angle is taken on the floor plane
 * only, because the model turns about the vertical, and it is negated from the
 * plain atan2(dz, dx): three.js yaw is counter-clockwise seen from above, while
 * atan2 on X/Z runs clockwise. Without the sign flip the model turns against the
 * student's hands. Returns a NaN angle when the points are too close on the
 * floor plane to have a heading (see MIN_TWIST_SPAN_M).
 */
export function sampleFromPoints(a: Vec3Like, b: Vec3Like): TwoPointSample {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const floorSpan = Math.hypot(dx, dz);
  return {
    distance: Math.hypot(dx, dy, dz),
    angle: floorSpan >= MIN_TWIST_SPAN_M ? Math.atan2(-dz, dx) : NaN,
  };
}

/**
 * Move `pos` so that scaling its object from `oldScale` to `newScale` happens
 * about `pivot` instead of about the object's own origin.
 *
 * An asset's origin is wherever its author left it — often at its feet, or off
 * to one side — so scaling about it makes the model grow into the dock or slide
 * sideways away from the student. Scaling about the bounding-box centre keeps
 * the model where the student is looking. Mutates and returns `pos`, like
 * three.js vector methods, so a THREE.Vector3 can be updated without
 * allocating; a non-positive or non-finite `oldScale` leaves it unchanged.
 */
export function scaleAboutPivot<T extends Vec3Like>(
  pos: T,
  pivot: Vec3Like,
  oldScale: number,
  newScale: number
): T {
  if (!(oldScale > 0) || !Number.isFinite(oldScale) || !Number.isFinite(newScale)) return pos;
  const ratio = newScale / oldScale;
  pos.x = pivot.x + (pos.x - pivot.x) * ratio;
  pos.y = pivot.y + (pos.y - pivot.y) * ratio;
  pos.z = pivot.z + (pos.z - pivot.z) * ratio;
  return pos;
}
